import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import Project from "./utils/Project";
import ProjectModal from "./utils/ProjectModal";
import Modal from "../Modal.js";
import { AnimatePresence, motion } from "framer-motion";

const StyledDiv = styled.div`
  width: 100vw;
  min-height: 50vh;
  padding: 6rem 1.25rem 3rem 1.25rem;

  @media (min-width: 1024px) {
    padding-right: 2.25rem;
  }
`;

const StyledListItem = styled.li`
  background: transparent;
  color: #111;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  text-align: center;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: #3b82f6;
    color: white;
    background-image: linear-gradient(to right, #818cf8, #60a5fa);
    transition: all 0.2s ease-in-out;
  }

  ${({ activeItem }) =>
    activeItem &&
    css`
      color: #3b82f6;
      color: white;
      background-image: linear-gradient(to right, #818cf8, #60a5fa);
      transition: all 0.2s ease-in-out;
    `}
`;

const StyledH1 = styled.h1`
  text-align: left;
  font-weight: 700;
  padding-bottom: 10px;
  position: relative;
`;

const Projects = ({ isModalOpen, setIsModalOpen }) => {
  const categories = [
    {
      id: 1,
      dataFilter: "All",
    },
    {
      id: 2,
      dataFilter: "Featured",
    },
    {
      id: 3,
      dataFilter: "Frontend",
    },
    {
      id: 4,
      dataFilter: "Fullstack",
    },
  ];
  const [activeList, setActiveList] = useState(1);
  const [activeCategory, setActiveCategory] = useState("All");
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [activeProject, setActiveProject] = useState();

  useEffect(() => {
    const projects = [
      {
        id: 1,
        title: "Spychain",
        subTitle: "A Cryptocurrency Portfolio Tracker",
        madeWith: ["React JS", "Node JS", "Tailwind CSS", "MongoDB", "Redux"],
        description:
          "A fullstack web application that tracks your cryptocurrency transactions. It performs multiple calculations in order to track your gains and lossess in your portfolio.",
        features: [
          "Fully Reponsive.",
          "Has a login system.",
          "Lazy loaded components for optimum performance.",
          "Track your gains and lossess easily.",
          "Supports different currencies.",
          "Can have multiple portfolios in one account.",
        ],
        github: "https://github.com/Gambit42/SpyChain",
        demo: "https://spychain.netlify.app/",
        image: "spychain.png",
        category: ["Fullstack", "Featured"],
        isPrivate: false,
      },
      {
        id: 2,
        title: "Pokedéx on the Web",
        subTitle: "View your favorite pokemons.",
        madeWith: ["React JS", "Styled Components", "Tailwind CSS", "Redux"],
        description:
          "A simple web application that uses Poké API in order to fetch the list of pokemon to be displayed in a neat way.",
        features: [
          "Available search functionality.",
          "Lazy loaded images for optimum performance.",
          "Paginated list of pokemons.",
        ],
        github: "https://github.com/Gambit42/Pokedex",
        demo: "https://pokedex-on-the-web.netlify.app/",
        image: "pokedex.png",
        category: "Frontend",
        isPrivate: false,
      },
      {
        id: 3,
        title: "Aniworld",
        subTitle: "Add animes into your collection.",
        madeWith: [
          "React JS",
          "Typescript",
          "Styled Components",
          "Tailwind CSS",
          "Firebase",
        ],
        description:
          "A web application that allows users to store or delete anime from their collection. It uses an external API to fetch the list of anime titles.",
        features: [
          "Login System.",
          "Add or delete animes from your collection.",
          "Available search functionality.",
          "Skeleton loader.",
          "Paginated list.",
        ],
        demo: "https://aniworld-web.netlify.app/",
        github: "https://github.com/Gambit42/AniWorld",
        image: "aniworld.png",
        category: ["Fullstack", "Featured"],
        isPrivate: false,
      },
      {
        id: 4,
        title: "Polemos Clone",
        subTitle: "A defi guild landing page clone.",
        madeWith: ["React JS", "Node JS", "Tailwind CSS", "MongoDB"],
        description:
          "A defi guild landing page clone. I added my own design flair in this website to not fully copy it. This is only frontend so the functionalities are not available.",
        features: ["Fully responsive website."],
        demo: "https://polemos-guild.netlify.app/",
        github: "https://github.com/Gambit42/Polemos_Clone",
        image: "polemos.png",
        category: "Frontend",
        isPrivate: false,
      },
      {
        id: 5,
        title: "OJT Portfolio",
        subTitle: "My internship experience.",
        madeWith: ["Next JS", "Tailwind CSS", "Styled Components"],
        description:
          "This a website that I made as part of our ojt documentation for one of my school subjects. Due to the nature of contents in this website I had to make it private.",
        features: ["Fully responsive website."],
        image: "ojt.png",
        category: ["Frontend", "Featured"],
        isPrivate: true,
      },
    ];

    const handleFilter = () => {
      if (activeCategory === "All") {
        setFilteredProjects(projects);
        return;
      }
      const filtered = projects.filter((project) =>
        project.category.includes(activeCategory)
      );

      setFilteredProjects(filtered);
    };
    handleFilter();
  }, [activeCategory]);

  return (
    <StyledDiv id="projects">
      <div className="w-full flex flex-col">
        <div className="flex flex-col justify-center items-center pb-0 mx-auto">
          <StyledH1 className="font-rubik font-bold text-4xl pt-5">
            My Projects
          </StyledH1>
          <p className="pt-2 font-karla w-4/5 text-gray-700 text-base mx-auto text-center">
            These are the projects that I made while learning how to create
            websites.
          </p>
        </div>
        <div className="mt-10 w-full flex justify-center items-center">
          <ul className="w-full grid gap-4 grid-cols-2 sm:w-4/5 md:grid-cols-3 lg:grid-cols-5 ">
            {categories.map((category) => (
              <StyledListItem
                className="w-36 font-karla text-sm sm:text-base font-medium text-black mx-auto"
                key={category.id}
                activeItem={activeList === category.id ? true : false}
                data-filter={category.dataFilter}
                onClick={() => {
                  setActiveCategory(category.dataFilter);
                  setActiveList(category.id);
                }}
              >
                {category.dataFilter}
              </StyledListItem>
            ))}
          </ul>
        </div>
      </div>
      <motion.div
        layout="position"
        className="mt-10 mx-auto flex flex-col justify-center items-center sm:grid sm:grid-cols-2 sm:gap-x-7 lg:grid-cols-3 lg:w-11/12 2xl:w-4/5 overflow-y-hidden"
      >
        <AnimatePresence>
          {filteredProjects.map((filteredProject) => (
            <Project
              key={filteredProject.id}
              setIsModalOpen={setIsModalOpen}
              project={filteredProject}
              setActiveProject={setActiveProject}
            />
          ))}
        </AnimatePresence>
      </motion.div>
      <Modal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen}>
        <ProjectModal
          activeProject={activeProject}
          setIsModalOpen={setIsModalOpen}
        />
      </Modal>
    </StyledDiv>
  );
};

export default Projects;
